// ===================================================================
// SHELTER — External Referrals
// Track referrals to FEMA, Red Cross, SBA, mental health, etc.
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Textarea,
  Modal, Stack, SimpleGrid, Table, ActionIcon, Drawer, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useNavigate } from 'react-router-dom';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { REFERRAL_TYPES } from '../../types/shelter';
import type { ShelterReferral, ReferralStatus } from '../../types/shelter';

const STATUS_COLORS: Record<ReferralStatus, string> = {
  pending: '#C68700', in_progress: 'var(--color-primary-700)', completed: 'var(--color-success)', declined: 'var(--color-error)',
};

const STATUS_OPTIONS: { value: ReferralStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
];

const EMPTY: Partial<ShelterReferral> = {
  survivorId: '', survivorName: '', referralType: 'other',
  agency: '', caseNumber: '', status: 'pending', notes: '',
};

export default function Referrals() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    referrals, survivors, loadingReferrals,
    loadReferrals, loadSurvivors,
    createReferral, updateReferral,
  } = useShelterStore();

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterReferral | null>(null);
  const [form, setForm] = useState<Partial<ShelterReferral>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ShelterReferral | null>(null);

  useEffect(() => { loadReferrals(); loadSurvivors(); }, [incidentId, loadReferrals, loadSurvivors]);

  // Store already server-side filters by active incident; use directly
  const iReferrals = incidentId ? referrals : [];
  const iSurvivors = incidentId ? survivors : [];

  const survivorSelectData = useMemo(() => iSurvivors.map(s => ({
    value: String(s.id), label: `${s.firstName} ${s.lastName}`,
  })), [iSurvivors]);

  const referralTypeLabel = (val: string) => REFERRAL_TYPES.find(t => t.value === val)?.label ?? val;

  const filtered = useMemo(() => {
    let list = iReferrals;
    if (typeFilter && typeFilter !== 'all') list = list.filter(r => r.referralType === typeFilter);
    if (statusFilter && statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.survivorName.toLowerCase().includes(q) ||
        r.agency.toLowerCase().includes(q) ||
        r.caseNumber.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iReferrals, typeFilter, statusFilter, search, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof ShelterReferral, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (r: ShelterReferral) => {
    setEditTarget(r);
    setForm({ ...r });
    setModalOpen(true);
  };

  const handleSurvivorSelect = (val: string | null) => {
    if (!val) return;
    const sv = iSurvivors.find(s => String(s.id) === val);
    setForm(prev => ({
      ...prev,
      survivorId: val,
      survivorName: sv ? `${sv.firstName} ${sv.lastName}` : val,
    }));
  };

  const handleSave = async () => {
    if (!form.survivorId?.trim()) {
      notifications.show({ message: 'Survivor is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        dateMade: editTarget ? form.dateMade : new Date().toISOString(),
        madeBy: editTarget ? form.madeBy : (user?.fullName ?? ''),
      };

      if (editTarget) {
        // Allow status updates with completion date
        if (form.status === 'completed' && !form.completedDate) {
          payload.completedDate = new Date().toISOString();
        }
        await updateReferral(editTarget.id, payload);
        notifications.show({ message: 'Referral updated', color: 'var(--color-success)' });
      } else {
        await createReferral(payload);
        notifications.show({ message: 'Referral created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">External Referrals</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingReferrals} onClick={() => loadReferrals()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Referral</Button>}
        </Group>
      </Group>

      <Group gap="xs" mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <Select size="xs" placeholder="Type" data={[{ value: 'all', label: 'All Types' }, ...REFERRAL_TYPES]}
          value={typeFilter} onChange={setTypeFilter} clearable w={180} />
        <Select size="xs" placeholder="Status" data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter} onChange={setStatusFilter} clearable w={160} />
      </Group>

      <Paper withBorder radius="md">
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
        <Table striped highlightOnHover>
          <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
            <Table.Tr>
              <Table.Th>Survivor</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('referralType')}>Referral Type{sortIcon('referralType')}</Table.Th>
              <Table.Th>Agency</Table.Th>
              <Table.Th>Case #</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" ta="center" py="lg">No referrals</Text></Table.Td></Table.Tr>
            )}
            {pageRows.map(r => (
              <Table.Tr key={r.id} onClick={() => setDetailTarget(r)} style={{ cursor: 'pointer' }}>
                <Table.Td fw={500}>{r.survivorName || '-'}</Table.Td>
                <Table.Td><Badge size="xs" variant="light">{referralTypeLabel(r.referralType)}</Badge></Table.Td>
                <Table.Td>{r.agency || '-'}</Table.Td>
                <Table.Td>{r.caseNumber || '-'}</Table.Td>
                <Table.Td>
                  <Badge size="xs" color={STATUS_COLORS[r.status]}>{(r.status || '').replace(/_/g, ' ')}</Badge>
                </Table.Td>
                <Table.Td>{r.dateMade ? new Date(r.dateMade).toLocaleDateString() : '-'}</Table.Td>
                {canEdit && (
                <Table.Td onClick={e => e.stopPropagation()}>
                  <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(r)}>
                    <IconPencil size={14} />
                  </ActionIcon>
                </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </div>
      </Paper>

      <Text size="xs" c="dimmed" mt="xs">{filtered.length} referral{filtered.length !== 1 ? 's' : ''}</Text>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} position="right" size="md"
        title={<Text fw={700} size="lg">Referral Details</Text>}>
        {detailTarget && (
          <Stack gap="md">
            <Group>
              <Badge variant="light">{referralTypeLabel(detailTarget.referralType)}</Badge>
              <Badge color={STATUS_COLORS[detailTarget.status]}>{(detailTarget.status || '').replace(/_/g, ' ')}</Badge>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Survivor</Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>{detailTarget.survivorName || '-'}</Text>
                {detailTarget.survivorId && (
                  <Button size="compact-xs" variant="subtle" onClick={() => { setDetailTarget(null); navigate(`/shelter/survivor/${detailTarget.survivorId}`); }}>
                    View Survivor
                  </Button>
                )}
              </Group>
            </div>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Agency</Text>
                <Text size="sm">{detailTarget.agency || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Case Number</Text>
                <Text size="sm" ff="monospace">{detailTarget.caseNumber || '-'}</Text>
              </div>
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Date Made</Text>
                <Text size="sm">{detailTarget.dateMade ? new Date(detailTarget.dateMade).toLocaleDateString() : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Completed Date</Text>
                <Text size="sm">{detailTarget.completedDate ? new Date(detailTarget.completedDate).toLocaleDateString() : '-'}</Text>
              </div>
            </SimpleGrid>

            <div>
              <Text size="xs" c="dimmed">Made By</Text>
              <Text size="sm">{detailTarget.madeBy || '-'}</Text>
            </div>

            {detailTarget.notes && (
              <div>
                <Text size="xs" c="dimmed">Notes</Text>
                <Text size="sm">{detailTarget.notes}</Text>
              </div>
            )}

            <Divider />

            {canEdit && (
            <Group justify="flex-end">
              <Button variant="light" leftSection={<IconPencil size={14} />} onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                Edit
              </Button>
            </Group>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Referral' : 'New Referral'} size="lg">
        <Stack gap="sm">
          <Select label="Survivor" data={survivorSelectData} value={form.survivorId ?? ''}
            onChange={handleSurvivorSelect} searchable required />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Referral Type" data={REFERRAL_TYPES.map(t => ({ value: t.value, label: t.label }))}
              value={form.referralType ?? 'other'} onChange={v => setField('referralType', v ?? 'other')} required />
            <TextInput label="Agency" value={form.agency ?? ''}
              onChange={e => setField('agency', e.target.value)} />
          </SimpleGrid>
          <TextInput label="Case Number" value={form.caseNumber ?? ''}
            onChange={e => setField('caseNumber', e.target.value)} />
          {editTarget && (
            <Select label="Status" data={STATUS_OPTIONS} value={form.status ?? 'pending'}
              onChange={v => setField('status', v ?? 'pending')} />
          )}
          <Textarea label="Notes" rows={3} value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editTarget ? 'Update' : 'Create'}</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
